import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import CardMission from "../../../components/Cac/Partner/Utils/MonitoringElements/CardMission";
import useSorting from "../../../hooks/useSorting";
import {
  MissionMonitoring,
} from "../../../interfaces/missionsMonitoring";
import FilterManagerMonitoringMission from "../../../components/Cac/Partner/Utils/MonitoringElements/FilterManagerMonitoringMission";
import FilterCategoryMonitoringMission from "../../../components/Cac/Partner/Utils/MonitoringElements/FilterCategoryMonitoringMission";
import { getMissionsOverviewByIdPartner } from "../../../services/Api/partnerService";
import { ToastContainer } from "react-toastify";
import SideBar from "../../../components/Layout/SideBar";
import Header from "../../../components/Layout/Header";
import Footer from "../../../components/Layout/Footer";
import SearchBar from "../../../components/Admin/Services/SearchBar";
import { useServiceStore } from "../../../stores/admin/service";
import { showToastError } from "../../../services/Toasts/toasts";

const PartnerMonitoringMission = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [missions, setMissions] = useState<MissionMonitoring[]>([]);
  const [managers, setManagers] = useState<string[]>([]);
  const [filteredData, setFilteredData] = useState<MissionMonitoring[]>([]);
  const [selectedResponsable, setSelectedResponsable] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [sortedData, handleSort] = useSorting(missions);
  const { idPartner } = useParams();
  const { searchTerm } = useServiceStore();

  useEffect(() => {
    if (idPartner)
      getMissionsOverviewByIdPartner(Number(idPartner))
        .then((data) => {
          const newManagers = [
            ...new Set(
              data?.map((mission: any) => mission?.fullName) as string[]
            ),
          ];
          const newCategories = [
            ...new Set(
              data?.map((mission: any) => mission?.categoryMission) as string[]
            ),
          ];
          // Ajouter le total pour chaque mission
          data?.forEach((mission: any) => {
            mission.missions?.forEach((missionItem: any) => {
              if (missionItem.name === "counts" && missionItem.value) {
                // Calculer le total des valeurs numériques
                const total = Object.values(missionItem.value).reduce((sum: number, value: any) => {
                  return sum + (typeof value === 'number' ? value : 0);
                }, 0);
                // Ajouter le total à l'objet value
                missionItem.value.total = total;
              }
            });
          });
          setMissions(data);
          setManagers(newManagers);
          setCategories(newCategories);
        })
        .catch(() => showToastError(t("common.error")));
  }, []);

  useEffect(() => {
    let tempData = [...sortedData]; // Copy of sortedData

    // Appliquer la recherche d'abord
    if (searchTerm) {
      tempData = tempData.filter(
        (mission: any) =>
          mission?.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          mission?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Puis appliquer les filtres
    if (selectedResponsable) {
      tempData = tempData.filter(
        (mission) => mission?.fullName === selectedResponsable
      );
    }

    if (selectedCategory) {
      tempData = tempData.filter(
        (mission) => mission?.categoryMission === selectedCategory
      );
    }

    // Seulement appeler setFilteredData si tempData a changé
    if (JSON.stringify(tempData) !== JSON.stringify(filteredData)) {
      setFilteredData(tempData);
    }
  }, [sortedData, selectedResponsable, selectedCategory, searchTerm, filteredData]);

  const handleResetFilter = () => {
    setSelectedResponsable("");
    setSelectedCategory("");
  };


  return (
    <div className="w-screen h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col justify-start items-start overflow-hidden">
      <Header />
      <div className="w-full flex justify-start items-start">
        <SideBar />
        <ToastContainer position="top-center" />
        <div className="flex flex-col justify-start items-center w-11/12 h-screen m-auto">
          {/* Header Section avec design moderne */}
          <div className="w-full bg-white rounded-2xl shadow-lg p-4 my-3 border border-gray-100">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-trio rounded-xl">
                  <svg className="w-6 h-6 text-grey-trio" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-700 font-K2D">
                    {t("partner.missionsMonitoringTitle")} <span className="text-yellow-trio">({filteredData.length})</span>
                  </h1>
                  <p className="text-gray-600 text-md mt-1">
                    {t("partner.missionsMonitoringSubtitle")}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 cursor-pointer"
                  onClick={() => navigate(-1)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  {t("common.back")}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-dark-lavender text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 cursor-pointer"
                  onClick={() => navigate(`/partner/${idPartner}/customers`)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {t("partner.customerFiles")}
                </button>
              </div>
            </div>
          </div>

          {/* Section des filtres avec design moderne */}
          <div className="w-full bg-white rounded-2xl shadow-lg p-4 mb-3 border border-gray-100">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-700 font-K2D">{t("partner.filtersAndSort")}</h2>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-4">
              <div className="flex flex-wrap gap-2">
                <SearchBar />
                <div className="flex items-center gap-2">
                  <FilterManagerMonitoringMission
                    managers={managers}
                    selectedResponsable={selectedResponsable}
                    setSelectedResponsable={setSelectedResponsable}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <FilterCategoryMonitoringMission
                    categories={categories}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleSort("dueDate")}
                className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-all duration-200 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t("customerRequests.date")}
              </button>
              <button
                type="button"
                onClick={() => handleSort("progress")}
                className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-all duration-200 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {t("partner.progress")}
              </button>
              <button
                type="button"
                onClick={() => handleSort("color")}
                className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-all duration-200 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                {t("partner.urgency")}
              </button>
              <button
                type="button"
                onClick={() => handleResetFilter()}
                className="px-3 py-2 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 transition-all duration-200 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t("common.reset")}
              </button>
            </div>
          </div>

          {/* Zone de contenu principal avec design moderne */}
          <div className="w-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex-1 max-h-[calc(95vh-20rem)]">
            <div className="p-4 h-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 overflow-y-auto h-full">
                {filteredData?.map((mission: any, index: any) => (
                  <CardMission key={index} mission={mission} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PartnerMonitoringMission;
